import {Component} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {CpmCLocationApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {IBaseModalComponent} from '../../../../sdk/src/components/ibase-modal/ibase-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {LocationUtils} from '../../utils/location.util';
import {CpmCLocationApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {firstValueFrom, Subscription} from 'rxjs';
import {CpmCLocationTreeApiView} from '../../../../gen/cpm-c-api-ts/src/views/c-customer/cpmc-location-tree.api-view';
import {TreeNode} from 'primeng/api';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';

/**
 * Configuration de la modale de stratégie de suppression
 */
export class LocationDeletionStrategyModalConfig {
    constructor(
        public location: CpmCLocationApiModel,
        public availableLocations: CpmCLocationApiModel[]
    ) {}
}

/**
 * Location deletion strategy modal result
 *
 * @param targetLocationUuid - The UUID of the target location for reassignment (null = cascade delete)
 */
export class LocationDeletionStrategyModalResult {
    constructor(public targetLocationUuid: string | null) {}
}

/**
 * Location deletion with strategy modal component
 *
 * This modal allows users to choose a deletion strategy when deleting a location that has children.
 * Available options:
 * - CASCADE: Delete the location and all its descendants (targetLocationUuid = null)
 * - REASSIGN: Reassign children to another specified location (targetLocationUuid = uuid)
 *
 * The modal includes a confirmation field where users must type the location name to confirm the deletion.
 */
@Component({
    selector: 'app-location-deletion-strategy-modal',
    templateUrl: './location-deletion-strategy-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule, FormsModule]
})
export class LocationDeletionStrategyModalComponent extends IBaseModalComponent<
    LocationDeletionStrategyModalComponent,
    LocationDeletionStrategyModalConfig
> {
    protected contentMinHeight = '20rem';

    protected readonly LocationUtils = LocationUtils;

    protected isCascadeDelete = true;
    protected totalDescendantsCount = -1;
    protected availableLocationsTree: TreeNode<CpmCLocationTreeApiView> = {children: []};
    protected currentParentUUID?: string;

    private fullLocationTree?: CpmCLocationTreeApiView[];

    private isCascadeSubscription?: Subscription;

    // ===============================================================
    // ===============================================================

    constructor() {
        super();
        this.config.width = '45rem';
        this.config.header = 'Delete Location with Children';
        this.config.closable = true;
        this.config.closeOnEscape = true;
        this.config.dismissableMask = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            isCascade: new FormControl<boolean>(true, [Validators.required]),
            targetLocation: new FormControl<string | null>({value: null, disabled: true}),
            confirmation: new FormControl<string>('', [
                Validators.required,
                (control) => {
                    const value = control.value;
                    const expectedValue = this.config.data?.location.name ?? '';
                    return value === expectedValue ? null : {confirmationMismatch: true};
                }
            ])
        });

        this.fullLocationTree = (await firstValueFrom(CpmCLocationApiService.getLocationTree())).data;

        await this.buildAvailableLocationsTree();

        this.isCascadeSubscription = this.form.controls['isCascade'].valueChanges.subscribe((isCascade) => {
            this.isCascadeDelete = isCascade;
            if (isCascade) {
                // Cascade mode - target location not needed
                this.form.controls['targetLocation'].clearValidators();
                this.form.controls['targetLocation'].setValue(null);
                this.form.controls['targetLocation'].disable();
            } else {
                // Reassign mode - target location is required
                this.form.controls['targetLocation'].setValidators([Validators.required]);
                this.form.controls['targetLocation'].enable();
            }
            this.form.controls['targetLocation'].updateValueAndValidity();
        });

        this.updateTotalDescendantsCount(this.fullLocationTree, this.config.data?.location.uuid, true);
    }

    /**
     * On destroy
     */
    protected override async onDestroy(): Promise<void> {
        this.isCascadeSubscription?.unsubscribe();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Checks whether the form can be submitted
     *
     * Validates that:
     * - The form is valid
     * - For REASSIGN mode: available locations exist and one is selected
     *
     * @returns True if the form can be submitted, false otherwise
     */
    protected canSubmit(): boolean {
        if (!this.form.valid) {
            return false;
        }

        return (
            this.form.controls['isCascade'].value ||
            (this.isReassignChildrenAvailable() && !!this.form.controls['targetLocation'].value)
        );
    }

    /**
     * On submit
     */
    protected async onSubmit(): Promise<void> {
        if (!this.canSubmit()) return;

        const isCascade = this.form.controls['isCascade'].value;
        const targetTreeNode = this.form.controls['targetLocation'].value;

        const targetUuid = isCascade ? null : (targetTreeNode?.key as string);

        this.ref.close(new LocationDeletionStrategyModalResult(targetUuid));
    }

    /**
     * On cancel
     */
    protected async onCancel(): Promise<void> {
        this.ref.close(null);
    }

    /**
     * Recursively updates the total count of descendant locations
     *
     * Traverses the location tree to count all descendants of the specified location.
     *
     * @param tree - The complete location tree
     * @param location_uuid - The UUID of the location to count descendants for
     * @param reset - Whether to reset the counter before counting (default: false)
     */
    protected updateTotalDescendantsCount(
        tree: CpmCLocationTreeApiView[],
        location_uuid?: string,
        reset = false
    ): void {
        if (reset) {
            this.totalDescendantsCount = 0;
        }
        if (location_uuid === undefined) {
            return;
        }
        for (const location of tree) {
            if (location.parent_uuid !== location_uuid) {
                continue;
            }
            this.totalDescendantsCount++;
            this.updateTotalDescendantsCount(tree, location.uuid);
        }
    }

    /**
     * Builds the hierarchical tree structure for available reassignment locations
     *
     * @throws Error if fullLocationTree is not loaded
     * @private
     */
    private async buildAvailableLocationsTree(): Promise<void> {
        const locationToDelete = this.config.data?.location;
        this.currentParentUUID = locationToDelete?.parent_uuid;

        const availableLocations = this.config.data?.availableLocations ?? [];
        if (!this.fullLocationTree) {
            throw new Error('Full location tree not loaded');
        }

        const availableUUIDs = new Set(availableLocations.map((loc) => loc.uuid));
        const requiredUUIDs = new Set<string>();

        availableUUIDs.forEach((uuid) => requiredUUIDs.add(uuid));

        this.fullLocationTree.forEach((loc) => {
            if (availableUUIDs.has(loc.uuid)) {
                let currentParent = loc.parent_uuid;
                while (currentParent) {
                    requiredUUIDs.add(currentParent);
                    const parentLoc = this.fullLocationTree!.find((l) => l.uuid === currentParent);
                    currentParent = parentLoc?.parent_uuid;
                }
            }
        });

        const filteredTree = this.fullLocationTree.filter((loc) => requiredUUIDs.has(loc.uuid));

        const selectableTypes = Array.from(new Set(availableLocations.map((loc) => loc.type)));

        LocationUtils.buildLocationHierarchyTree(filteredTree, this.availableLocationsTree, [], selectableTypes);

        if (!locationToDelete) return;

        const ancestorUUIDs = new Set<string>();
        let currentParent = locationToDelete.parent_uuid;

        while (currentParent) {
            ancestorUUIDs.add(currentParent);
            const parentLoc = this.fullLocationTree.find((l) => l.uuid === currentParent);
            currentParent = parentLoc?.parent_uuid;
        }

        TreeUtils.visitTree(this.availableLocationsTree.children!, (node) => {
            if (ancestorUUIDs.has(node.key as string)) {
                node.expanded = true;
            }
        });
    }

    /**
     * Checks if the "Reassign children to another location" option is available
     *
     * @returns True if at least one compatible location is available for reassignment, false otherwise
     */
    protected isReassignChildrenAvailable(): boolean {
        return (this.availableLocationsTree.children?.length ?? 0) > 0;
    }

    /**
     * Dialog service parameters helper
     */
    public static getDialogServiceParameters(data?: LocationDeletionStrategyModalConfig): {
        modal?: boolean;
        data?: LocationDeletionStrategyModalConfig;
    } {
        return {
            modal: true,
            data
        };
    }
}
